import { useEffect, useState } from "react";
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { requireAuth } from "@/lib/auth/require-auth";
import { requireOrg } from "@/lib/auth/require-role";
import AppHeader from "@/layout/AppHeader";
import AppSidebar from "@/layout/AppSidebar";
import Breadcrumbs from "@/components/ui/breadcrumbs/breadcrumbs";
import { SidebarController } from "@/providers/SidebarController";
import { useSidebarMode, useCurrentOrg } from "@/stores";
import { cn } from "@/utils";
import { RealtimeProvider } from "@/lib/realtime";
import {
  BulkProgressDrawer,
  readDrawerOpenState,
} from "@/features/media/components/bulk-progress-drawer";
import { BulkProgressFooterPill } from "@/features/media/components/bulk-progress-footer-pill";
import { getDisabledFeatureForPath, useOrgFeatures } from "@/features/features";
import { useMediaStorageReadiness } from "@/features/media/hooks/use-media-storage-readiness";
import FeatureAnnouncementModal from "@/components/shared/FeatureAnnouncementModal";

// Admin shell: auth + org gates + sidebar + header. Sidebar width is driven
// by the sidebar store so collapse state persists across routes.
export const Route = createFileRoute("/_admin")({
  beforeLoad: async ({ location }) => {
    await requireAuth(location.href);
    await requireOrg();
  },
  component: AdminLayout,
});

function AdminLayout() {
  const mode = useSidebarMode();
  const location = useLocation();
  const navigate = useNavigate();
  const currentOrg = useCurrentOrg();
  const enabledFeatures = useOrgFeatures();
  const mediaStorage = useMediaStorageReadiness({ enabled: !!currentOrg });
  const mediaProgressEnabled = mediaStorage.data?.ready === true;
  // Drawer open state is sessionStorage-hydrated so F5 mid-batch keeps the
  // drawer visible. Lifted here because the footer pill (sibling) toggles it.
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(() => readDrawerOpenState());
  // Margin matches sidebar width on xl+ so main content never sits under the rail.
  const contentMargin = mode === "collapsed" ? "xl:ml-16" : "xl:ml-[252px]";
  const isKGWorkspace = location.pathname.startsWith("/kg-graph");

  useEffect(() => {
    if (location.pathname.startsWith("/feature-disabled")) return;
    const disabledFeature = getDisabledFeatureForPath(location.pathname, enabledFeatures);
    if (!disabledFeature) return;
    void navigate({
      to: "/feature-disabled/$code",
      params: { code: disabledFeature },
      replace: true,
    });
  }, [enabledFeatures, location.pathname, navigate]);

  // Multiplexed SSE provider mounts at the auth+org boundary so a single
  // EventSource per tab carries media/kg/kb/bulk/permission/workflow events
  // for descendant components (Phase 12 L5). orgId guard avoids opening a
  // connection before the org store hydrates.
  return (
    <RealtimeProvider orgId={currentOrg?.id ?? null}>
      <div className="flex h-screen min-w-0 overflow-hidden bg-[var(--background)]">
        <SidebarController />
        <AppSidebar />
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col transition-[margin-left] duration-200 ease-out",
            contentMargin,
          )}
        >
          <AppHeader />
          <main
            id="main-content"
            className="no-scrollbar min-w-0 flex-1 overflow-y-auto p-4 md:p-6"
          >
            <div
              className={cn("mx-auto min-w-0 w-full", isKGWorkspace ? "max-w-none" : "max-w-7xl")}
            >
              <Breadcrumbs />
              <Outlet />
            </div>
          </main>
        </div>
        {/* Bulk progress UI (Phase 13) — global so it survives route changes. */}
        {mediaProgressEnabled && (
          <>
            <BulkProgressFooterPill
              enabled={mediaProgressEnabled}
              onOpen={() => setBulkDrawerOpen(true)}
            />
            <BulkProgressDrawer
              enabled={mediaProgressEnabled}
              open={bulkDrawerOpen}
              onOpenChange={setBulkDrawerOpen}
            />
          </>
        )}
        <FeatureAnnouncementModal />
      </div>
    </RealtimeProvider>
  );
}
